import { createFileLogger } from '../../utils/logger';

const log = createFileLogger('GA4_SERVICE');

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

interface QueuedGA4Event {
  eventName: string;
  properties: Record<string, any>;
}

class GA4Service {
  private isInitialized = false;
  private isReady = false;
  // measurementId is set dynamically via GA4Provider.init() from configService.
  // This fallback is only used if the provider path is bypassed.
  private measurementId = '';

  // Batches gtag('event', ...) calls instead of firing one at the instant
  // each track()/trackPageView() call happens — same 2s debounce / 50-event
  // cap as ServerRelayProvider (see providers/serverRelay.ts), so a burst of
  // rapid-fire events (e.g. a fast-typing session) collapses into one flush
  // instead of one gtag() dispatch per keystroke.
  private eventQueue: QueuedGA4Event[] = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly batchMs = 2000;
  private readonly maxBatch = 50;

  setMeasurementId(id: string): void {
    this.measurementId = id;
    if (this.isReady && typeof window !== 'undefined' && window.gtag) {
      window.gtag('config', id);
    }
  }

  init(): void {
    if (this.isInitialized) {
      // Expected in React StrictMode dev — second effect run, safe to ignore
      log.debug('GA4 init called again (no-op)');
      return;
    }

    try {
      if (typeof window.gtag === 'function') {
        this.isInitialized = true;
        this.isReady = true;
        log.info('GA4 initialized successfully');
      } else {
        log.warn('GA4 gtag not found - gtag.js may not have loaded yet');
        
        this.waitForGtag();
      }
    } catch (error) {
      log.error('Failed to initialize GA4:', error);
      throw error;
    }
  }

  private waitForGtag(): void {
    let attempts = 0;
    const maxAttempts = 10;
    const checkInterval = 100;

    const checkGtag = () => {
      attempts++;
      if (typeof window.gtag === 'function') {
        this.isInitialized = true;
        this.isReady = true;
        log.info('GA4 initialized successfully (delayed)');
      } else if (attempts < maxAttempts) {
        setTimeout(checkGtag, checkInterval);
      } else {
        log.error('GA4 gtag not available after waiting');
      }
    };

    setTimeout(checkGtag, checkInterval);
  }

  track(eventName: string, properties?: Record<string, any>): void {
    if (!this.isReady) {
      log.debug('GA4 not ready, skipping track:', eventName);
      return;
    }
    this.enqueueEvent(eventName, properties || {});
  }

  identify(userId: string): void {
    if (!this.isReady) {
      log.debug('GA4 not ready, skipping identify:', userId);
      return;
    }

    try {
      if (window.gtag) {
        window.gtag('config', this.measurementId, {
          user_id: userId
        });
        log.debug('Identified user:', userId);
      }
    } catch (error) {
      log.error('Failed to identify user:', userId, error);
    }
  }

  setUserProperties(properties: Record<string, any>): void {
    if (!this.isReady) {
      log.debug('GA4 not ready, skipping set user properties');
      return;
    }

    try {
      if (window.gtag) {
        window.gtag('set', 'user_properties', properties);
        log.debug('Set user properties:', properties);
      }
    } catch (error) {
      log.error('Failed to set user properties:', error);
    }
  }

  trackPageView(pageTitle?: string, pagePath?: string): void {
    if (!this.isReady) {
      log.debug('GA4 not ready, skipping page view');
      return;
    }
    this.enqueueEvent('page_view', {
      page_title: pageTitle,
      page_path: pagePath || window.location.pathname
    });
  }

  getInitializationStatus(): boolean {
    return this.isReady;
  }

  async flushEvents(): Promise<void> {
    if (!this.isInitialized) {
      log.debug('GA4 not initialized, skipping flush');
      return;
    }

    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    this.flushQueue();

    return new Promise((resolve) => {
      setTimeout(() => {
        log.debug('Events flushed - safe to navigate');
        resolve();
      }, 300);
    });
  }

  private enqueueEvent(eventName: string, properties: Record<string, any>): void {
    this.eventQueue.push({ eventName, properties });
    if (this.eventQueue.length >= this.maxBatch) {
      this.flushQueue();
    } else {
      this.scheduleFlush();
    }
  }

  private scheduleFlush(): void {
    if (this.flushTimer) return;
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      this.flushQueue();
    }, this.batchMs);
  }

  private flushQueue(): void {
    if (this.eventQueue.length === 0) return;
    const batch = this.eventQueue.splice(0, this.eventQueue.length);

    try {
      if (window.gtag) {
        for (const { eventName, properties } of batch) {
          window.gtag('event', eventName, properties);
        }
        log.debug(`Flushed ${batch.length} GA4 event(s)`);
      }
    } catch (error) {
      log.error('Failed to flush GA4 event batch:', error);
    }
  }
}

const ga4Service = new GA4Service();

export { ga4Service };
export default ga4Service;

